/* tslint:disable:no-console max-line-length */
import {Injectable, OnDestroy} from '@angular/core';
import {ActivityData} from '@docma-mm-core/activity/activity';
import {ActivityApiService} from '@docma-mm-core/activity/activity-api.service';
import {ActivityDbService} from '@docma-mm-core/activity/db/activity-db.service';
import {MultiActivityData} from '@docma-mm-core/activity/multi-activity';
import {ActivitySyncService} from '@docma-mm-core/activity/sync/activity-sync.service';
import {FaultPersistenceService} from '@docma-mm-core/fault/fault-persistence.service';
import {FaultOperationPathEnum} from '@docma-mm-core/fault/patch/fault-operation-path.enum';
import {FaultPatch} from '@docma-mm-core/fault/patch/fault-patch';
import {FaultPatchService} from '@docma-mm-core/fault/patch/fault-patch-service';
import {FaultSyncQueuedItem} from '@docma-mm-core/fault/sync/fault-sync-queued-item';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {UntilDestroy} from '@ngneat/until-destroy';
import {GroupData} from './group';
import {GroupApiService} from './group-api.service';
import {GroupDbService} from './group-db.service';
import {AttachmentSyncService} from '@docma-mm-core/attachment/attachment-sync.service';
import {Subject} from 'rxjs';
import {ActivityEditData} from '@docma-mm-core/activity/activity-edit';
import {ActivityPatchModeEnum} from '@docma-mm-core/activity/activity-patch-mode.enum';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {SyncResult} from '@docma-mm-core/model/sync-state/sync-result';
import {ErrorService} from '../../mm-faults/mm-error.service';
import {Logger} from '@mydocma/common/src/lib/logger';
import {modifiedOnOfSyncResult} from '@docma-mm-core/fault/sync/base-fault-sync.service';

@UntilDestroy()
@Injectable()
export class GroupSyncService implements OnDestroy {

  constructor(private readonly patchService: FaultPatchService,
              private readonly persistenceService: FaultPersistenceService,
              private readonly apiService: GroupApiService,
              private readonly groupDbService: GroupDbService,
              private readonly activityDbService: ActivityDbService,
              private readonly activitySyncService: ActivitySyncService,
              private readonly activityApiService: ActivityApiService,
              private readonly attachmentSyncService: AttachmentSyncService,
              private readonly errorService: ErrorService,
              private readonly logger: Logger) {
  }

  private multiActivitySyncedSubject: Subject<MultiActivityData> = new Subject<MultiActivityData>();
  multiActivitySynced$ = this.multiActivitySyncedSubject.asObservable();
  private activityEditSyncedSubject: Subject<ActivityEditData> = new Subject<ActivityEditData>();
  activityEditSynced$ = this.activityEditSyncedSubject.asObservable();

  ngOnDestroy() {
  }

  public async syncGroupFromPatches(project: {mydocmaUid: string, projectId: number}, syncItems: FaultSyncQueuedItem[], patches: FaultPatch[]): Promise<SyncResult> {
    let groupUid;
    let error: string;
    let groupError: string;

    try {
      const gUids = new Set(syncItems.map(x => x.groupUid));
      if (gUids.size > 1) {
        throw new Error('Only sync items of one gUid could be passed at once!');
      }

      console.info('### syncGroupFromPatches 01');

      groupUid = [...gUids][0];

      if (patches.length === 0 || patches.length !== syncItems.length) {
        return;
      }

      const groupPatches = patches.filter(f => f.operation.path === FaultOperationPathEnum.group);
      const activityPatches = patches.filter(f => f.operation.path === FaultOperationPathEnum.groupActivity);

      const groupedGroupActivityPatches = groupActivities(activityPatches);
      const faultMap: Map<string, string> = new Map<string, string>();
      const activities: { [id: number]: { Id: number, UniqueId: string, Fault: FaultData } } = [];
      const syncingFaultUids: string[] = [];

      for (const key of Object.keys(groupedGroupActivityPatches)) {
        const patchesInternal = groupedGroupActivityPatches[key] as FaultPatch[];
        if (!faultMap.has(patchesInternal[0].oUid)) {
          faultMap.set(patchesInternal[0].oUid, patchesInternal[0].fUid);
        }
        if (!activities.hasOwnProperty(patchesInternal[0].oUid)) {
          activities[patchesInternal[0].oUid] = {
            Id: patchesInternal[0].oId,
            UniqueId: patchesInternal[0].oUid,
            Fault: new FaultData({Id: null, UniqueId: patchesInternal[0].fUid})
          };
        }
        if (syncingFaultUids.findIndex(x => x === patchesInternal[0].fUid) === -1) {
          syncingFaultUids.push(patchesInternal[0].fUid);
        }
      }

      console.info('### syncGroupFromPatches 02');

      await this.persistenceService.saveSyncStates(project.mydocmaUid,
        syncingFaultUids,
        new SyncState({status: SyncStatusEnum.Syncing, date: new Date(Date.now())}));

      console.info('### syncGroupFromPatches 03');

      await this.groupDbService.saveSyncState(project.mydocmaUid, groupUid, new SyncState({status: SyncStatusEnum.Syncing}));
      const groupChangeState = this.patchService.generateChangeStateFromPatches(groupPatches);

      console.info('### syncGroupFromPatches 04');

      let groupLoadState;
      const hasError = false;
      const result: SyncResult = new SyncResult();
      const saveFaultLoadState = false;

      try {
        if (groupPatches && groupPatches.length > 0) {
          groupLoadState = await this.syncGroupDataFromPatches(project, groupPatches);
        } else {
          groupLoadState = await this.groupDbService.getLoadState(project.mydocmaUid, groupUid);
        }
      } catch (err) {
        this.logger.error(err, '', 'GroupSyncService');
        groupError = this.errorService.getError(err);

        throw err;
      }

      result.groupLoadStates.push(groupLoadState);

      console.info('### syncGroupFromPatches 05');

      const hasActivityError = false;

      try {
        if (Object.values(groupedGroupActivityPatches).length > 0) {
          const firstPatch: FaultPatch = Object.values(groupedGroupActivityPatches)[0][0] as FaultPatch;
          const patchMode = (firstPatch.options ? firstPatch.options.ActivityPatchModeEnum : ActivityPatchModeEnum.Single);

          if (activityPatches && activityPatches.length > 0 && firstPatch.operation.op === 'add') {
            const activityLoadStates = await this.syncActivitiesDataFromPatches(project, activityPatches, faultMap);
            result.activityLoadStates.push(...activityLoadStates);
          } else if (activityPatches && activityPatches.length > 0 && firstPatch.operation.op === 'replace') {
            const activityLoadStates = await this.syncActivityEditDataFromPatches(project, activityPatches, Object.values(activities), patchMode);
            result.activityLoadStates.push(...activityLoadStates);
          }
        }
      } catch (err) {
        this.logger.error(err, '', 'GroupSyncService');
        error = this.errorService.getError(err);
        throw err;
      }

      console.info('### syncGroupFromPatches 06');

      const groupedAttachmentPatches = this.patchService
        .groupByOuid(patches.filter(p => p.operation.path === FaultOperationPathEnum.groupAttachment));

      const attachmentsSyncResult = await this.attachmentSyncService.syncActivityGroupAttachmentFromPatches(project.mydocmaUid,
        groupUid,
        groupLoadState?.data.id,
        groupedAttachmentPatches,
        faultMap);

      result.activityAttachmentsLoadStates.push(...attachmentsSyncResult.activityAttachmentsLoadStates);

      console.info('### syncGroupFromPatches 07');

      const faultLoadedStates = await this.persistenceService.getLoadStates(project.mydocmaUid, syncingFaultUids);
      result.faultLoadStates = faultLoadedStates;

      const res = modifiedOnOfSyncResult(result);
      if (res.saveFaultLoadState) {
        result.modifiedOn = res.modifiedOn;
        result.modifier = res.modifier;
      }

      /* finally set sync state */
      await this.persistenceService.saveSyncStates(project.mydocmaUid,
        syncingFaultUids,
        new SyncState({status: SyncStatusEnum.Synced, date: new Date(Date.now())}));

      console.info('### syncGroupFromPatches 08');

      if (result.activityLoadStates) {
        const activityItems = result.activityLoadStates.map(x => ({activityUid: x.data.UniqueId, faultUid: faultMap.get(x.data.UniqueId)}));

        await this.activityDbService.saveChangeStates(project.mydocmaUid, activityItems, new ChangeState<ActivityData>({status: ChangeStatusEnum.NotChanged}));
        await this.activityDbService.saveSyncStates(project.mydocmaUid, activityItems,
          new SyncState({status: SyncStatusEnum.Synced}));
      }

      console.info('### syncGroupFromPatches 09');

      await this.groupDbService.saveChangeState(project.mydocmaUid, groupUid, new ChangeState<GroupData>({status: ChangeStatusEnum.NotChanged}));

      const syncState = await this.groupDbService.saveSyncState(project.mydocmaUid,
        groupUid,
        new SyncState({status: SyncStatusEnum.Synced}));

      result.syncState = syncState;
      return result;
    } catch (err) {
      const syncStateErr = await this.groupDbService.saveSyncState(project.mydocmaUid,
        groupUid,
        new SyncState({status: SyncStatusEnum.Error, date: new Date(Date.now()), error: (groupError ?? error)}));
      return new SyncResult({syncState: syncStateErr});
    }
  }

  public async syncGroupDataFromPatches(project: {mydocmaUid: string, projectId: number},
                                        patches: FaultPatch[]): Promise<LoadState<GroupData>> {
    this.assertValidPatches(patches, FaultOperationPathEnum.group);

    let loadedState: LoadState<GroupData>;
    const groupOUid = patches[0].oUid;
    const groupUid = patches[0].gUid;
    const groupId = patches[0].gId;

    const groupChangeState = this.patchService.generateChangeStateFromPatches(patches);
    if (groupChangeState.status !== ChangeStatusEnum.NotChanged) {
      loadedState = await this.syncGroupChangeState(project, groupId, groupChangeState);
      await this.groupDbService.saveLoadState(project.mydocmaUid, groupUid, loadedState);
    }
    const comittedOperations = patches.map(p => p.operation.id);
    await this.patchService.remove(project.mydocmaUid, comittedOperations);
    return loadedState;
  }

  private async syncGroupChangeState(project: {mydocmaUid: string, projectId: number}, groupId: number,
                                     changeState: ChangeState<GroupData>): Promise<LoadState<GroupData>> {
    if (changeState.status === ChangeStatusEnum.New) {
      const loadedData = await this.apiService.createGroup(project, changeState.data).toPromise();
      return new LoadState<GroupData>({ status: LoadStatusEnum.Loaded, data: loadedData });
    }
  }

  public async syncActivitiesDataFromPatches(project: {mydocmaUid: string, projectId: number}, patches: FaultPatch[], faultMap: Map<string, string>): Promise<LoadState<ActivityData>[]> {
    this.assertValidActivityPatches(patches, FaultOperationPathEnum.groupActivity);
    let loadedStates: LoadState<ActivityData>[];
    const changeStates: ChangeState<any>[] = [];

    const groupedGroupActivityPatches = groupActivities(patches);

    const activityItems = Object.keys(groupedGroupActivityPatches).map(x => {
      const patchesInternal = groupedGroupActivityPatches[x] as FaultPatch[];
      const changeStateInternal = this.patchService.generateChangeStateFromPatches(patchesInternal);
      changeStates.push(changeStateInternal);

      return ({ activityUid: patchesInternal[0].oUid, faultUid: patchesInternal[0].fUid });
    });

    await this.activityDbService.saveSyncStates(project.mydocmaUid,
      activityItems,
      new SyncState({ status: SyncStatusEnum.Syncing }));

    if (changeStates.filter(x => x.status !== ChangeStatusEnum.NotChanged).length > 0) {
      loadedStates = await this.syncActivitiesChangeState(project,
                                                             faultMap,
                                                             changeStates.filter(x => x.status !== ChangeStatusEnum.NotChanged));

      const loadedStatesParam = loadedStates.map(x => ({ activityUid: x.data.UniqueId, faultUid: faultMap.get(x.data.UniqueId), loadState: x}));
      await this.activityDbService.saveLoadStates(project.mydocmaUid, loadedStatesParam);
    }
    const comittedOperations = patches.map(p => p.operation.id);
    await this.patchService.remove(project.mydocmaUid, comittedOperations);

    return loadedStates;
  }

  public async syncActivityEditDataFromPatches(project: {mydocmaUid: string, projectId: number}, patches: FaultPatch[], activities: {Id: number, UniqueId: string, Fault: FaultData}[], patchMode: ActivityPatchModeEnum): Promise<LoadState<ActivityData>[]> {
    this.assertValidActivityPatches(patches, FaultOperationPathEnum.groupActivity);
    let saveResponse: {LoadedStates: LoadState<ActivityData>[], Changes: ActivityData};
    const changeStates: ChangeState<any>[] = [];

    const groupedGroupActivityPatches = groupActivities(patches);

    const activityItems = Object.keys(groupedGroupActivityPatches).map(x => {
      const patchesInternal = groupedGroupActivityPatches[x] as FaultPatch[];
      const changeStateInternal = this.patchService.generateChangeStateFromPatches(patchesInternal);
      changeStates.push(changeStateInternal);

      return ({ activityUid: patchesInternal[0].oUid, faultUid: patchesInternal[0].fUid });
    });

    await this.activityDbService.saveSyncStates(project.mydocmaUid,
      activityItems,
      new SyncState({ status: SyncStatusEnum.Syncing }));

    if (changeStates.filter(x => x.status !== ChangeStatusEnum.NotChanged).length > 0) {
      saveResponse = await this.syncActivityEditChangeState(project,
        patchMode,
        activities,
        changeStates.filter(x => x.status !== ChangeStatusEnum.NotChanged));
      const loadedStatesParam = saveResponse.LoadedStates.map(x => ({ activityUid: x.data.UniqueId, faultUid: x.data.Fault.UniqueId, loadState: x}));
      await this.activityDbService.saveLoadStates(project.mydocmaUid, loadedStatesParam);
    }
    const comittedOperations = patches.map(p => p.operation.id);
    await this.patchService.remove(project.mydocmaUid, comittedOperations);

    return saveResponse.LoadedStates;
  }

  private async syncActivitiesChangeState(project: {mydocmaUid: string, projectId: number},
                                          faultMap: Map<string, string>,
                                          changeStates: ChangeState<any>[]): Promise<LoadState<ActivityData>[]> {

    const newChangeStates = changeStates.filter(x => x.status === ChangeStatusEnum.New);

    if (newChangeStates.length > 0) {
      const changedActivities = newChangeStates.map(x => x.data);
      const multiActivityData = new MultiActivityData(changedActivities, faultMap);
      multiActivityData.activities = null;

      console.info('### createActivityGroup ###');

      const saveResponse = await this.activityApiService.createActivityGroup(project, multiActivityData).toPromise();

      await Promise.all(saveResponse.FaultActivities.map(async savedActivity => {
        const act = changedActivities.find(a => a.UniqueId === savedActivity.Activity.UniqueId);
        if (act && act?.Id != savedActivity.Activity?.Id) {
          act.Id = savedActivity.Activity.Id;
        }
      }));

      /* pass full activities of changeStates for response */
      saveResponse.activities = changedActivities;

      this.multiActivitySyncedSubject.next(saveResponse);

      console.info('### finished createActivityGroup ###');

      const loadStates: LoadState<ActivityData>[] = [];
      saveResponse.activities.forEach(a => loadStates.push(new LoadState<ActivityData>({ status: LoadStatusEnum.Loaded, data: a})));

      return loadStates;
    }
    // else if (changeState.status === ChangeStatusEnum.Changed) {
    //   const updatedData = await this.apiService.updateActivity(groupId, changeState.data).toPromise();
    //   return new LoadState<ActivityData>({ status: LoadStatusEnum.Loaded, data: updatedData });
    // }
  }

  private async syncActivityEditChangeState(project: {mydocmaUid: string, projectId: number},
                                            mode: ActivityPatchModeEnum,
                                            activities: {Id: number, UniqueId: string, Fault: FaultData}[],
                                            changeStates: ChangeState<any>[]): Promise<{LoadedStates: LoadState<ActivityData>[], Changes: ActivityData}> {

    const changedChangedStates = changeStates.filter(x => x.status === ChangeStatusEnum.Changed);

    if (changedChangedStates.length > 0) {
      const changes = changedChangedStates.map(x => x.data)[0];
      const activityEditData = new ActivityEditData(changes, mode, activities, null);

      console.info('### syncActivityEditChangeState ###');

      const patchResponse = await this.activityApiService.patchActivities(project, activityEditData).toPromise();
      console.info('### finished syncActivityEditChangeState ###');

      const loadStates: LoadState<ActivityData>[] = [];
      [...patchResponse.activities, ...patchResponse.activitiesOfGroup].forEach(a => loadStates.push(new LoadState<ActivityData>({ status: LoadStatusEnum.Loaded, data: a})));

      // notify to faults-loader..
      this.activityEditSyncedSubject.next(patchResponse);

      return {LoadedStates: loadStates, Changes: changes};
    }
  }

  private assertValidPatches(patches: FaultPatch[], path: FaultOperationPathEnum) {
    const groupUid = patches[0].gUid;
    const oUid = patches[0].oUid;
    for (const p of patches) {
      if (p.gUid !== groupUid || p.oUid !== oUid || p.operation.path !== path) {
        throw new Error(`Can't sync group ${groupUid}/${oUid}. Sync patches not valid`);
      }
    }
  }

  private assertValidActivityPatches(patches: FaultPatch[], path: FaultOperationPathEnum) {
    const groupUid = patches[0].gUid;
    const fUid = patches[0].fUid;
    for (const p of patches) {
      if (p.gUid !== groupUid || p.operation.path !== path) {
        throw new Error(`Can't sync group activity ${groupUid}/${p.oUid}. Sync patches not valid`);
      }
    }
  }

  private assertValidAttachmentPatches(patches: FaultPatch[], path: FaultOperationPathEnum) {
    const groupUid = patches[0].gUid;
    for (const p of patches) {
      if (p.gUid !== groupUid || p.operation.path !== path) {
        throw new Error(`Can't sync group attachment ${groupUid}/${p.oUid}. Sync patches not valid`);
      }
    }
  }

}

function groupActivities(activityPatches: FaultPatch[]) {
  return activityPatches.reduce((acc, curr) => {
    if (curr.oUid) {
      const key = curr.fUid + '#' + curr.oUid;
      if (acc[key] == null) {
        acc[key] = [];
      }
      acc[key].push(curr);
    }
    return acc;
  }, {});
}

